import { useCallback, useEffect, useRef, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { Lifebuoy, Plus, PaperPlaneRight, X, Paperclip, CircleNotch, File as FileIcon } from "@phosphor-icons/react";

const STATUS_STYLE = {
    open: "bg-blue-100 text-blue-700",
    in_progress: "bg-amber-100 text-amber-700",
    resolved: "bg-green-100 text-green-700",
    closed: "bg-stone-200 text-stone-600",
};
const PRIORITY_STYLE = {
    low: "text-stone-500", normal: "text-stone-600", high: "text-orange-600", urgent: "text-red-600",
};
const fmt = (s) => (s ? new Date(s).toLocaleString() : "");
const isImg = (m) => (m || "").startsWith("image/");
const attHref = (id) => `${API_BASE}/support/attachments/${id}`;

async function uploadFiles(fileList) {
    const out = [];
    for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/support/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
        out.push(data);
    }
    return out;
}

function AttachmentList({ items }) {
    if (!items || items.length === 0) return null;
    return (
        <div className="mt-2 flex flex-wrap gap-2" data-testid="message-attachments">
            {items.map((a) => (
                isImg(a.mime) ? (
                    <a key={a.id} href={attHref(a.id)} target="_blank" rel="noreferrer" className="block">
                        <img src={attHref(a.id)} alt={a.filename} className="h-24 w-24 rounded-lg border border-stone-200 object-cover" />
                    </a>
                ) : (
                    <a key={a.id} href={attHref(a.id)} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2 text-xs font-semibold text-ink hover:bg-stone-50">
                        <FileIcon size={16} className="text-brand" />
                        <span className="max-w-[160px] truncate">{a.filename}</span>
                    </a>
                )
            ))}
        </div>
    );
}

function AttachChips({ items, onRemove }) {
    if (!items.length) return null;
    return (
        <div className="mt-2 flex flex-wrap gap-2">
            {items.map((a) => (
                <span key={a.id} className="inline-flex items-center gap-1.5 rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-ink">
                    <FileIcon size={13} /> <span className="max-w-[160px] truncate">{a.filename}</span>
                    <button onClick={() => onRemove(a.id)} className="text-stone2 hover:text-red-600"><X size={12} weight="bold" /></button>
                </span>
            ))}
        </div>
    );
}

export default function Support() {
    const { user } = useAuth();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNew, setShowNew] = useState(false);
    const [active, setActive] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/support/tickets");
            setTickets(data.tickets || []);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <AppShell>
            <div className="mx-auto max-w-6xl px-6 py-8 lg:px-10" data-testid="support-page">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-2 text-brand">
                            <Lifebuoy size={22} weight="duotone" />
                            <span className="text-xs font-bold uppercase tracking-widest">Priority Support</span>
                        </div>
                        <h1 className="mt-1 font-heading text-4xl font-bold tracking-tight">Support tickets</h1>
                        <p className="mt-1 text-sm text-stone2">Raise an issue and our team will respond here.</p>
                    </div>
                    <button
                        data-testid="new-ticket-btn"
                        onClick={() => setShowNew(true)}
                        className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5"
                    >
                        <Plus size={16} weight="bold" /> New ticket
                    </button>
                </div>

                <div className="mt-8 space-y-3">
                    {loading ? (
                        <div className="py-16 text-center text-sm text-stone2">Loading…</div>
                    ) : tickets.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-stone-300 py-20 text-center" data-testid="support-empty">
                            <p className="text-sm text-stone2">No tickets yet. Raise your first one.</p>
                        </div>
                    ) : (
                        tickets.map((t) => (
                            <button
                                key={t.id}
                                data-testid={`ticket-row-${t.id}`}
                                onClick={() => setActive(t)}
                                className="flex w-full items-center justify-between gap-4 rounded-2xl border border-stone-200 bg-white px-6 py-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-md"
                            >
                                <div className="min-w-0">
                                    <div className="truncate text-lg font-semibold">{t.subject}</div>
                                    <div className="mt-1 text-xs text-stone2">
                                        #{t.id.slice(0, 8)} · <span className={PRIORITY_STYLE[t.priority]}>{t.priority}</span> · updated {fmt(t.updated_at)}
                                    </div>
                                </div>
                                <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold capitalize ${STATUS_STYLE[t.status] || ""}`}>
                                    {(t.status || "").replace("_", " ")}
                                </span>
                            </button>
                        ))
                    )}
                </div>

                {showNew && <NewTicketModal onClose={() => setShowNew(false)} onCreated={(t) => { setTickets((x) => [t, ...x]); setShowNew(false); setActive(t); }} />}
                {active && <TicketDrawer ticket={active} onClose={() => setActive(null)} onUpdated={load} me={user} />}
            </div>
        </AppShell>
    );
}

function NewTicketModal({ onClose, onCreated }) {
    const [subject, setSubject] = useState("");
    const [category, setCategory] = useState("technical");
    const [priority, setPriority] = useState("normal");
    const [message, setMessage] = useState("");
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    const onPick = async (e) => {
        const files = e.target.files;
        if (!files?.length) return;
        setUploading(true);
        try {
            const up = await uploadFiles(files);
            setAttachments((x) => [...x, ...up]);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const submit = async () => {
        if (subject.trim().length < 3 || message.trim().length < 3) {
            toast.error("Add a subject and a short description.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/support/tickets", { subject, category, priority, message, attachments });
            toast.success("Ticket raised");
            onCreated(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" data-testid="new-ticket-modal">
            <div className="w-full max-w-2xl rounded-2xl bg-white p-8 shadow-2xl">
                <div className="flex items-center justify-between">
                    <h2 className="font-heading text-2xl font-bold">New support ticket</h2>
                    <button onClick={onClose} className="text-stone2 hover:text-ink" data-testid="new-ticket-close"><X size={22} /></button>
                </div>
                <div className="mt-5 space-y-4">
                    <label className="block text-xs font-semibold text-stone2">Subject
                        <input data-testid="ticket-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={140}
                            className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm outline-none focus:border-ink" placeholder="Short summary of the issue" />
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                        <label className="block text-xs font-semibold text-stone2">Category
                            <select data-testid="ticket-category-select" value={category} onChange={(e) => setCategory(e.target.value)}
                                className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm outline-none focus:border-ink">
                                <option value="technical">Technical</option>
                                <option value="billing">Billing</option>
                                <option value="feature">Feature request</option>
                                <option value="other">Other</option>
                            </select>
                        </label>
                        <label className="block text-xs font-semibold text-stone2">Priority
                            <select data-testid="ticket-priority-select" value={priority} onChange={(e) => setPriority(e.target.value)}
                                className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm outline-none focus:border-ink">
                                <option value="low">Low</option>
                                <option value="normal">Normal</option>
                                <option value="high">High</option>
                                <option value="urgent">Urgent</option>
                            </select>
                        </label>
                    </div>
                    <label className="block text-xs font-semibold text-stone2">Description
                        <textarea data-testid="ticket-message-input" value={message} onChange={(e) => setMessage(e.target.value)} rows={7} maxLength={5000}
                            className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm outline-none focus:border-ink" placeholder="Describe what's happening, steps to reproduce, and any error messages." />
                    </label>
                    <div>
                        <input ref={fileRef} type="file" multiple hidden onChange={onPick} data-testid="ticket-file-input" />
                        <button type="button" data-testid="ticket-attach-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
                            className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-4 py-2 text-xs font-semibold text-stone-600 hover:bg-stone-100 disabled:opacity-50">
                            {uploading ? <CircleNotch size={15} className="animate-spin" /> : <Paperclip size={15} />}
                            {uploading ? "Uploading…" : "Attach files"}
                        </button>
                        <AttachChips items={attachments} onRemove={(id) => setAttachments((x) => x.filter((a) => a.id !== id))} />
                    </div>
                </div>
                <div className="mt-6 flex justify-end gap-2">
                    <button onClick={onClose} className="rounded-full border border-stone-300 px-4 py-2.5 text-sm font-semibold text-stone-600 hover:bg-stone-100">Cancel</button>
                    <button data-testid="ticket-submit-btn" onClick={submit} disabled={busy || uploading}
                        className="rounded-full bg-brand px-6 py-2.5 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-50">
                        {busy ? "Sending…" : "Raise ticket"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function TicketDrawer({ ticket, onClose, onUpdated }) {
    const [t, setT] = useState(ticket);
    const [body, setBody] = useState("");
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get(`/support/tickets/${ticket.id}`);
            setT(data);
        } catch { /* ignore */ }
    }, [ticket.id]);

    useEffect(() => { refresh(); }, [refresh]);

    const onPick = async (e) => {
        const files = e.target.files;
        if (!files?.length) return;
        setUploading(true);
        try {
            const up = await uploadFiles(files);
            setAttachments((x) => [...x, ...up]);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const send = async () => {
        if (!body.trim() && attachments.length === 0) return;
        setBusy(true);
        try {
            await api.post(`/support/tickets/${t.id}/reply`, { body, attachments });
            setBody("");
            setAttachments([]);
            await refresh();
            onUpdated?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" data-testid="ticket-drawer" onClick={onClose}>
            <div className="flex h-full w-full max-w-lg flex-col bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between border-b border-stone-200 p-6">
                    <div className="min-w-0">
                        <div className="truncate font-heading text-xl font-bold">{t.subject}</div>
                        <div className="mt-1 flex items-center gap-2 text-xs text-stone2">
                            <span className={`rounded-full px-2 py-0.5 font-bold capitalize ${STATUS_STYLE[t.status] || ""}`}>{(t.status || "").replace("_", " ")}</span>
                            <span className="capitalize">{t.category}</span> · <span className={`capitalize ${PRIORITY_STYLE[t.priority]}`}>{t.priority}</span>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-stone2 hover:text-ink" data-testid="ticket-drawer-close"><X size={22} /></button>
                </div>

                <div className="flex-1 space-y-4 overflow-y-auto p-6" data-testid="ticket-thread">
                    {(t.messages || []).map((m) => {
                        const mine = m.author_role === "workspace";
                        return (
                            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                                <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${mine ? "bg-brand/10 text-ink" : "bg-stone-100 text-ink"}`}>
                                    <div className="mb-0.5 text-[10px] font-bold uppercase tracking-wide text-stone2">
                                        {m.author_role === "support" ? "Inspiro Support" : (m.author_name || "You")}
                                    </div>
                                    {m.body ? <div className="whitespace-pre-wrap">{m.body}</div> : null}
                                    <AttachmentList items={m.attachments} />
                                    <div className="mt-1 text-[10px] text-stone2">{fmt(m.created_at)}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {t.status === "closed" ? (
                    <div className="border-t border-stone-200 p-3 text-center text-xs text-stone2">This ticket is closed. Reply to reopen it.</div>
                ) : null}
                <div className="border-t border-stone-200 p-4">
                    <AttachChips items={attachments} onRemove={(id) => setAttachments((x) => x.filter((a) => a.id !== id))} />
                    <div className="mt-2 flex items-end gap-2">
                        <input ref={fileRef} type="file" multiple hidden onChange={onPick} data-testid="reply-file-input" />
                        <button type="button" data-testid="reply-attach-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
                            className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-stone-300 text-stone-600 hover:bg-stone-100 disabled:opacity-50">
                            {uploading ? <CircleNotch size={17} className="animate-spin" /> : <Paperclip size={17} />}
                        </button>
                        <textarea data-testid="ticket-reply-input" value={body} onChange={(e) => setBody(e.target.value)} rows={2}
                            className="flex-1 resize-none rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-ink" placeholder="Write a reply…" />
                        <button data-testid="ticket-reply-send" onClick={send} disabled={busy || uploading || (!body.trim() && attachments.length === 0)}
                            className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-brand text-white hover:bg-brand-hover disabled:opacity-40">
                            <PaperPlaneRight size={18} weight="fill" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
